import { Json, StoreStructure } from '../types/types';
import { toSingular } from './singular';

export const isCollection = (value: Json) => {
  if (typeof value !== 'object') return false;
  if (typeof value === 'undefined') return false;
  const items = Object.values(value);
  const ok = items.every((item) => isObject(item) && 'id' in item);
  return isObject(value) && ok;
};

export const isObject = (value: Json) => {
  return typeof value === 'object' && !Array.isArray(value);
};

export const identifyTransient = <T extends Json>(initialState: T) => {
  const root = Object.keys(initialState).reduce((acc, key) => {
    acc[key] = false;
    return acc;
  }, {} as Json);

  const traverse = (state: Json, xpath: string[]) => {
    const keys = Object.keys(state ?? {});

    for (const key of keys) {
      const value = state[key];

      if (isCollection(value)) {
        const singleKey = toSingular(key);

        if (xpath.length > 0 && key in root) {
          root[key] = true;
        }

        if (singleKey in root) {
          root[singleKey] = true;
        }

        const id = Object.keys(value)[0];
        const item = value[id];
        traverse(item, [...xpath, key, id]);
      } else if (isObject(value)) {
        if (xpath.length > 0 && key in root) {
          root[key] = true;
        }
      }
    }
  };

  traverse(initialState, []);

  return Object.keys(root).filter((key) => root[key]);
};

export const analyzeTransient = <T extends Json>(initialState: T) => {
  const output: Json = {};
  const transient = identifyTransient(initialState);

  const mainState = Object.keys(initialState).reduce((acc, key) => {
    const isTransient = transient.includes(key);
    if (!isTransient && isCollection(initialState[key])) {
      acc[key] = initialState[key];
    }

    return acc;
  }, {} as Json);

  const { currentIds } = initialState;

  const traverse = (state: Json, xpath: string[]) => {
    const keys = Object.keys(state ?? {});

    for (const key of keys) {
      const value = state[key];

      if (isCollection(value)) {
        const singleKey = toSingular(key);
        const id = Object.keys(value)[0];
        const item = value[id];
        const idKey = `${singleKey}Id`;
        const newXPath = [...xpath, key];
        const newXPathWithId = [...xpath, key, `[${idKey}]`];

        if (idKey in currentIds) {
          if (singleKey in initialState) {
            output[singleKey] = newXPathWithId.join('.');
          }

          if (key in initialState && transient.includes(key)) {
            output[key] = [...xpath, key].join('.');
          }
        }

        traverse(item, newXPathWithId);
      } else if (isObject(value)) {
        const newXPath = [...xpath, key];
        output[key] = newXPath.join('.');
      } else {
      }
    }
  };

  traverse(mainState, []);

  return output as Record<keyof T, string>;
};

export const calcIdsOrder = (transient: Json) => {
  const arr = Object.keys(transient).map((key) => {
    const value = transient[key];
    const parts = value
      .split('.')
      .map((part: string) => {
        const regex = /\[(.*?)\]/;
        const match = part.match(regex);
        if (match) {
          return match[1];
        }
      })
      .filter((i: string) => i);

    return parts;
  });

  const longest = arr.sort((a: string[], b: string[]) => b.length - a.length)[0];

  return longest;
};

export const calcIdsToClear = (idsOrder: string[], key: string) => {
  const indexOf = idsOrder.indexOf(key);
  if (indexOf === -1) {
    return [];
  }

  return [...idsOrder].slice(indexOf + 1);
};

export const clearTransient = <T extends StoreStructure>(state: T) => {
  const transient = analyzeTransient(state);
  const keys = Object.keys(transient);

  for (const key of keys) {
    const isTransient = transient[key];
    if (isTransient) {
      delete state[key];
    }
  }

  return state as T;
};
